"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { TimelineEventHoverPayload } from "./TimelinePeriod";
import {
  getTimelineTooltipPosition,
  type TimelineTooltipDimensions,
  type TimelineTooltipPosition,
} from "./timeline-tooltip-position";
import type {
  HoveredEventTooltip,
  TimelineViewportState,
} from "./timeline-root-types";

const DEFAULT_TOOLTIP_DIMENSIONS: TimelineTooltipDimensions = {
  width: 210,
  height: 52,
};

export interface UseTimelineTooltipControllerInput {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  viewportState: TimelineViewportState;
}

export interface TimelineTooltipController {
  clearTimelineTooltip: () => void;
  handleHoverEvent: (payload: TimelineEventHoverPayload | null) => void;
  hoveredEventTooltip: HoveredEventTooltip | null;
  tooltipPosition: TimelineTooltipPosition | null;
  tooltipRef: RefObject<HTMLDivElement | null>;
}

function getTimelineTooltipMeasurementNode(input: {
  eventNode: HTMLDivElement;
}): HTMLDivElement {
  const nestedScrollableNode = input.eventNode.querySelector(".cscrollable-root");
  return nestedScrollableNode instanceof HTMLDivElement
    ? nestedScrollableNode
    : input.eventNode;
}

function hasTimelineTooltipOverflow(input: {
  measurementNode: HTMLDivElement;
}): boolean {
  const hasHorizontalOverflow =
    input.measurementNode.scrollWidth > input.measurementNode.clientWidth + 1;
  const hasVerticalOverflow =
    input.measurementNode.scrollHeight > input.measurementNode.clientHeight + 1;

  return hasHorizontalOverflow || hasVerticalOverflow;
}

function selectHoveredEventTooltip(input: {
  payload: TimelineEventHoverPayload;
  scrollContainerNode: HTMLDivElement;
}): HoveredEventTooltip {
  const eventRect = input.payload.element.getBoundingClientRect();
  const viewportRect = input.scrollContainerNode.getBoundingClientRect();
  const viewportAnchorX =
    eventRect.left - viewportRect.left + eventRect.width / 2;
  const viewportAnchorY = input.payload.isAbove
    ? eventRect.top - viewportRect.top
    : eventRect.bottom - viewportRect.top;

  return {
    key: `${input.payload.periodIndex}-${input.payload.eventIndex}`,
    text: input.payload.text,
    anchorX: viewportAnchorX + input.scrollContainerNode.scrollLeft,
    anchorY: viewportAnchorY + input.scrollContainerNode.scrollTop,
    preferAbove: input.payload.isAbove,
  };
}

export function useTimelineTooltipController({
  scrollContainerRef,
  viewportState,
}: UseTimelineTooltipControllerInput): TimelineTooltipController {
  const [hoveredEventTooltip, setHoveredEventTooltip] =
    useState<HoveredEventTooltip | null>(null);
  const [tooltipDimensions, setTooltipDimensions] =
    useState<TimelineTooltipDimensions>(DEFAULT_TOOLTIP_DIMENSIONS);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const clearTimelineTooltip = useCallback((): void => {
    setHoveredEventTooltip(null);
  }, []);

  const handleHoverEvent = useCallback(
    (payload: TimelineEventHoverPayload | null): void => {
      if (!payload) {
        clearTimelineTooltip();
        return;
      }

      const eventNode = payload.element;
      const measurementNode = getTimelineTooltipMeasurementNode({ eventNode });
      if (!hasTimelineTooltipOverflow({ measurementNode })) {
        clearTimelineTooltip();
        return;
      }

      const scrollContainerNode = scrollContainerRef.current;
      if (!scrollContainerNode) {
        return;
      }

      setHoveredEventTooltip(
        selectHoveredEventTooltip({
          payload,
          scrollContainerNode,
        }),
      );
    },
    [clearTimelineTooltip, scrollContainerRef],
  );

  const tooltipPosition = useMemo(
    () =>
      getTimelineTooltipPosition({
        tooltip: hoveredEventTooltip,
        viewportState,
        dimensions: tooltipDimensions,
      }),
    [hoveredEventTooltip, tooltipDimensions, viewportState],
  );

  const hoveredEventTooltipKey = hoveredEventTooltip?.key;
  useEffect(() => {
    const tooltipNode = tooltipRef.current;
    if (!tooltipNode || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const width = entry.contentRect.width;
      const height = entry.contentRect.height;

      setTooltipDimensions((previous) => {
        if (
          Math.abs(previous.width - width) < 0.5 &&
          Math.abs(previous.height - height) < 0.5
        ) {
          return previous;
        }

        return { width, height };
      });
    });

    resizeObserver.observe(tooltipNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [hoveredEventTooltipKey]);

  return {
    clearTimelineTooltip,
    handleHoverEvent,
    hoveredEventTooltip,
    tooltipPosition,
    tooltipRef,
  };
}
